import { Plugin, PluginKey } from 'prosemirror-state';

const emoticonPluginKey = new PluginKey('emoticonAutoReplace');

// ASCII emoticons agents actually type, mapped to the emoji they mean.
// Longer tokens are matched first, so ":-)" wins over ":)" and "8-)" over "8)".
export const EMOTICON_MAP = {
  ':)': '🙂',
  ':-)': '🙂',
  ':(': '🙁',
  ':-(': '🙁',
  ':D': '😀',
  ':-D': '😀',
  ';)': '😉',
  ';-)': '😉',
  ':P': '😛',
  ':-P': '😛',
  ':p': '😛',
  ':-p': '😛',
  ':O': '😮',
  ':-O': '😮',
  ':o': '😮',
  ':-o': '😮',
  ':|': '😐',
  ':-|': '😐',
  ':/': '😕',
  ':-/': '😕',
  '8)': '😎',
  '8-)': '😎',
  ":'(": '😢',
  '<3': '❤️',
};

const MAX_TOKEN_LENGTH = Object.keys(EMOTICON_MAP).reduce(
  (max, token) => Math.max(max, token.length),
  0
);

/**
 * Finds an emoticon ending at the caret.
 *
 * Exported for tests: it is pure, so the substitution rules can be verified
 * without standing up an editor.
 *
 * @param {string} textBefore Text already in the block, immediately before the caret.
 * @param {string} typedChar The character being typed now.
 * @returns {{token: string, emoji: string}|null} Match, or null when nothing applies.
 */
export const matchEmoticon = (textBefore, typedChar) => {
  if (typedChar.length !== 1) return null;

  const candidate = textBefore + typedChar;
  const maxLength = Math.min(candidate.length, MAX_TOKEN_LENGTH);
  if (maxLength < 2) return null;

  // Longest first, so ":-)" wins over ":)" and "8-)" over "8)".
  const lengths = Array.from(
    { length: maxLength - 1 },
    (_, index) => maxLength - index
  );

  const found = lengths
    .map(length => {
      const token = candidate.slice(candidate.length - length);
      const emoji = EMOTICON_MAP[token];
      if (!emoji) return null;

      // Only substitute at a word boundary, so "8:30" and a URL's "://" are
      // left alone while ":)" at the start of a line or after a space converts.
      const boundaryIndex = candidate.length - length - 1;
      const charBefore = boundaryIndex >= 0 ? candidate[boundaryIndex] : '';
      if (charBefore && !/\s/.test(charBefore)) return null;

      return { token, emoji };
    })
    .find(Boolean);

  return found || null;
};

// Emoticons inside code are almost always meant literally.
const isInsideCode = (state, pos) => {
  const $pos = state.doc.resolve(pos);

  const codeMark = state.schema.marks.code;
  if (codeMark && codeMark.isInSet(state.storedMarks || $pos.marks())) {
    return true;
  }

  const codeBlock = state.schema.nodes.code_block;
  if (!codeBlock) return false;

  for (let depth = $pos.depth; depth > 0; depth -= 1) {
    if ($pos.node(depth).type === codeBlock) return true;
  }

  return false;
};

// Replaces ASCII emoticons with emoji as the agent types, e.g. ":)" becomes 🙂.
//
// Undo is handled by backspace rather than the history plugin: pressing
// backspace immediately after a substitution puts the typed characters back,
// which is what people reach for. Plain undo would restore the half-typed
// emoticon and then convert again on the next keystroke, so it is not relied on.
export const emoticonPlugin = new Plugin({
  key: emoticonPluginKey,

  // Remembers the substitution that just happened, so backspace can reverse it.
  // Any other document change clears it.
  state: {
    init: () => null,
    apply(tr, value) {
      const replacement = tr.getMeta(emoticonPluginKey);
      if (replacement) return replacement;
      if (tr.docChanged) return null;
      return value;
    },
  },

  props: {
    handleTextInput(view, from, to, text) {
      if (text.length !== 1) return false;

      const { state } = view;
      if (isInsideCode(state, from)) return false;

      const $from = state.doc.resolve(from);
      const blockStart = $from.start();
      const lookBehindFrom = Math.max(blockStart, from - MAX_TOKEN_LENGTH);
      const textBefore = state.doc.textBetween(lookBehindFrom, from);

      const match = matchEmoticon(textBefore, text);
      if (!match) return false;

      // The typed character is not in the document yet, so the token occupies
      // everything from its start up to the caret.
      const tokenStart = from - (match.token.length - 1);
      if (tokenStart < blockStart) return false;

      const tr = state.tr.insertText(match.emoji, tokenStart, to);
      tr.setMeta(emoticonPluginKey, {
        from: tokenStart,
        to: tokenStart + match.emoji.length,
        token: match.token,
      });
      view.dispatch(tr);

      return true;
    },

    handleKeyDown(view, event) {
      if (event.key !== 'Backspace' || event.isComposing) return false;

      const last = emoticonPluginKey.getState(view.state);
      if (!last) return false;

      const { selection } = view.state;
      if (!selection.empty || selection.from !== last.to) return false;

      view.dispatch(view.state.tr.insertText(last.token, last.from, last.to));
      return true;
    },
  },
});

export default emoticonPlugin;
